import { Column } from '@qtek/shared/models';
import { Observable, OperatorFunction } from 'rxjs';
import { SearchPredicate } from '../models';

export interface QtDataSourceContext<T = any> {
  readonly valueAccessorFn: (row: T, path: string) => unknown;
  readonly sortValueAccessorFn: ((row: T, path: string) => unknown) | null;
  readonly searchPredicate: SearchPredicate;
  readonly columns$: Observable<Column[]>;
  readonly groupingColumns$: Observable<Column[]>;
}

export const PIPE_ORDER = {
  SEARCH: 10,
  FILTER: 20,
  SORT: 30,
  // 35 = reserved for length tap inside QtDataSource (not an adapter)
  PAGINATE: 40,
  GROUP_BY: 50,
} as const;

/**
 * Base interface every control adapter must implement.
 *
 * Contract:
 * - valueChanges$ emits ONLY on user-initiated changes.
 *   applyValue() must NEVER cause valueChanges$ to emit.
 * - persistent = false excludes adapter from state persistence/restore.
 * - key undefined = no de-dup guard.
 */
export interface QtControlAdapter<TState = unknown> {
  readonly key?: string;
  readonly persistent?: boolean;
  readonly valueChanges$: Observable<TState | null>;
  applyValue(value: TState): void;
  getValue(): TState | null;
  setContext?(context: QtDataSourceContext<any>): void;
}

/**
 * Adapter that also transforms the data pipeline.
 * pipeOrder controls position — use PIPE_ORDER constants.
 */
export interface QtDataTransformAdapter<T, TState = unknown>
  extends QtControlAdapter<TState> {
  readonly operator: OperatorFunction<T[], T[]>;
  readonly pipeOrder: number;
}

export function isDataTransformAdapter<T>(
  adapter: QtControlAdapter
): adapter is QtDataTransformAdapter<T> {
  return 'operator' in adapter && 'pipeOrder' in adapter;
}
